import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { DragDropProvider } from "@dnd-kit/react";
import { isSortableOperation, useSortable } from "@dnd-kit/react/sortable";
import { moveItem } from "../helpers/addressRouteHelpers";
import {
  GOOGLE_PLACES_MIN_QUERY_LENGTH,
  getGooglePlaceAddress,
  getGooglePredictions,
  isGooglePlacesOption,
  normalizeSearchText,
} from "../helpers/googlePlacesAutocompleteHelpers";

const SortableSubRouteItem = ({
  route,
  index,
  canRemove,
  onRemove,
  getAddressKey,
  subRoutesGroup,
}) => {
  const { ref, handleRef, isDragging, isDropTarget } = useSortable({
    id: `${getAddressKey(route)}-${index}`,
    index,
    group: subRoutesGroup,
  });

  return (
    <Box
      ref={ref}
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: isDropTarget ? "action.hover" : "background.paper",
        opacity: isDragging ? 0.6 : 1,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        ref={handleRef}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flex: 1,
          minWidth: 0,
          cursor: "grab",
        }}
      >
        <Typography variant="body2" noWrap sx={{ fontWeight: "bold" }}>
          {index + 1}. {route.name}
        </Typography>
        <DragHandleIcon sx={{ flexShrink: 0 }} />
      </Box>
      {canRemove && (
        <IconButton
          size="small"
          color="error"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRemove(index);
          }}
          onPointerDown={(event) => {
            event.stopPropagation();
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

SortableSubRouteItem.propTypes = {
  route: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  canRemove: PropTypes.bool.isRequired,
  onRemove: PropTypes.func.isRequired,
  getAddressKey: PropTypes.func.isRequired,
  subRoutesGroup: PropTypes.string.isRequired,
};

const SubRoutesModal = ({
  open,
  onClose,
  addressOrigin,
  subRoutesList,
  handleSubRoutesDragEnd,
  getAddressKey,
  getAddressLabel,
  handleRemoveSubRoute,
  subRoutesGroup,
  subRouteSelected,
  addressApi,
  handleAddSubRoute,
  handleOpenSubRouteMapModal,
  onAccept,
}) => {
  const [subRouteInputValue, setSubRouteInputValue] = useState("");
  const [googleSubRouteOptions, setGoogleSubRouteOptions] = useState([]);

  const availableSubRouteOptions = useMemo(
    () =>
      addressApi.filter(
        (route) =>
          getAddressKey(subRoutesList[subRoutesList.length - 1]) !==
          getAddressKey(route),
      ),
    [addressApi, getAddressKey, subRoutesList],
  );
  const subRouteSearchValue = normalizeSearchText(subRouteInputValue);
  const filteredSubRouteOptions = useMemo(() => {
    if (!subRouteSearchValue) return availableSubRouteOptions;

    return availableSubRouteOptions.filter((option) =>
      normalizeSearchText(getAddressLabel(option) || option?.name).includes(
        subRouteSearchValue,
      ),
    );
  }, [availableSubRouteOptions, getAddressLabel, subRouteSearchValue]);
  const shouldSearchGoogleSubRoute =
    subRouteSearchValue.length >= GOOGLE_PLACES_MIN_QUERY_LENGTH &&
    filteredSubRouteOptions.length === 0;
  const subRouteOptions = shouldSearchGoogleSubRoute
    ? googleSubRouteOptions
    : filteredSubRouteOptions;

  useEffect(() => {
    if (!shouldSearchGoogleSubRoute) {
      setGoogleSubRouteOptions([]);
      return undefined;
    }

    let isMounted = true;
    const timeoutId = window.setTimeout(async () => {
      try {
        const predictions = await getGooglePredictions(subRouteInputValue);
        if (isMounted) {
          setGoogleSubRouteOptions(predictions);
        }
      } catch (error) {
        console.log(error);
        if (isMounted) {
          setGoogleSubRouteOptions([]);
        }
      }
    }, 350);

    return () => {
      isMounted = false;
      window.clearTimeout(timeoutId);
    };
  }, [shouldSearchGoogleSubRoute, subRouteInputValue]);

  const getSubRouteOptionLabel = (option) => {
    if (!option) return "";
    if (typeof option === "string") return option;
    if (isGooglePlacesOption(option)) return option.description || option.name;
    return getAddressLabel(option) || option.name || "";
  };

  const handleSubRouteChange = async (event, newValue) => {
    if (!newValue) return;

    if (isGooglePlacesOption(newValue)) {
      try {
        const googleAddress = await getGooglePlaceAddress(newValue);
        handleAddSubRoute(googleAddress);
        setSubRouteInputValue("");
        setGoogleSubRouteOptions([]);
      } catch (error) {
        console.log(error);
      }
      return;
    }

    handleAddSubRoute(newValue);
    setSubRouteInputValue("");
  };

  const handleSubRoutesDragOver = (event) => {
    if (!isSortableOperation(event.operation)) return;

    const fromIndex = event.operation.source.initialIndex;
    const toIndex = event.operation.target?.index;
    if (
      !Number.isInteger(fromIndex) ||
      !Number.isInteger(toIndex) ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= subRoutesList.length ||
      toIndex >= subRoutesList.length
    ) return;

    const nextRoutes = moveItem(subRoutesList, fromIndex, toIndex);
    if (nextRoutes.some((route, index) =>
      index > 0 && getAddressKey(route) === getAddressKey(nextRoutes[index - 1]),
    )) {
      event.preventDefault();
    }
  };

  return (
  <Modal open={open} onClose={onClose} aria-labelledby="sub-routes-modal-title">
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        bgcolor: "background.paper",
        borderRadius: 3,
        boxShadow: 24,
        p: 2,
        width: {
          xs: "92%",
          sm: "560px",
        },
        maxHeight: "85vh",
        minHeight: "60vh",
        overflowY: "auto",
      }}
    >
      <Typography
        id="sub-routes-modal-title"
        variant="h6"
        component="h2"
        sx={{ mb: 2, fontWeight: "bold" }}
      >
        Mi Ruta
      </Typography>
      <Stack spacing={2}>
        <Box
          sx={{
            maxHeight: 220,
            overflowY: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "4px",
            mb: 2,
            p: 1,
          }}
        >
          <p
            style={{
              fontSize: 14,
              marginLeft: "-3px",
              marginTop: "-5px",
              fontWeight: "bold",
            }}
          >
            {" "}
            Origen:
          </p>
          <Typography variant="body2">{addressOrigin?.name || ""}</Typography>
        </Box>
      </Stack>

      <Stack spacing={2}>
        <DragDropProvider
          onDragOver={handleSubRoutesDragOver}
          onDragEnd={handleSubRoutesDragEnd}
        >
          <Stack spacing={1}>
            {subRoutesList.map((route, index) => (
              <SortableSubRouteItem
                key={`${getAddressKey(route)}-${index}`}
                route={route}
                index={index}
                canRemove={subRoutesList.length > 1}
                onRemove={handleRemoveSubRoute}
                getAddressKey={getAddressKey}
                subRoutesGroup={subRoutesGroup}
              />
            ))}
          </Stack>
        </DragDropProvider>

        <Autocomplete
          value={subRouteSelected}
          inputValue={subRouteInputValue}
          disablePortal
          name="subRouteSelected"
          id="combo-box-sub-route"
          onChange={handleSubRouteChange}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "reset") return;
            setSubRouteInputValue(newInputValue);
          }}
          filterOptions={(options) => options}
          options={subRouteOptions}
          getOptionLabel={getSubRouteOptionLabel}
          isOptionEqualToValue={(option, value) =>
            getSubRouteOptionLabel(option) === getSubRouteOptionLabel(value)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Seleccionar subruta"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Abrir mapa de subruta"
                        onMouseDown={(event) => {
                          event.preventDefault();
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleOpenSubRouteMapModal();
                        }}
                      >
                        <LocationOnIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            pt: 1,
          }}
        >
          <Button variant="outlined" color="error" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onAccept || onClose}
          >
            Aceptar
          </Button>
        </Box>
      </Stack>
    </Box>
  </Modal>
  );
};

SubRoutesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  addressOrigin: PropTypes.object,
  subRoutesList: PropTypes.array.isRequired,
  handleSubRoutesDragEnd: PropTypes.func.isRequired,
  getAddressKey: PropTypes.func.isRequired,
  getAddressLabel: PropTypes.func.isRequired,
  handleRemoveSubRoute: PropTypes.func.isRequired,
  subRoutesGroup: PropTypes.string.isRequired,
  subRouteSelected: PropTypes.object,
  addressApi: PropTypes.array.isRequired,
  handleAddSubRoute: PropTypes.func.isRequired,
  handleOpenSubRouteMapModal: PropTypes.func.isRequired,
  onAccept: PropTypes.func,
};

export default SubRoutesModal;
